import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export type NotificationType = 'ANNOUNCEMENT' | 'MAINTENANCE' | 'INFO' | 'WARNING' | 'COMMENT';
export type TargetType = 'ALL' | 'INSTALLATION' | 'GROUP' | 'USERS' | 'MIXED';

export interface Notification {
  key: number;
  type: NotificationType;
  title: string;
  body: string;
  priority?: number;
  icon?: string;
  targetType?: TargetType;
  published?: boolean;
  publishDate?: string;
  expiresAt?: string;
  dismissable?: boolean;
  refKey?: number;
  isRead?: boolean;
  readDate?: string;
  timestampIns?: string;
  uteIns?: string;
  uteVar?: string;
  timestampVar?: string;
}

export interface NotificationTarget {
  userKey?: number;
  installation?: string;
  groupKey?: number;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  private apiUrl: string;

  constructor(private http: HttpClient) {
    const apiBaseUrl = environment['API_BASE_URL'] || process.env.API_BASE_URL;
    this.apiUrl = apiBaseUrl + 'crm';
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders().set('Content-Type', 'application/json');
  }

  // ============================================
  // USER ENDPOINTS
  // ============================================

  getUnread(userKey: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/notifications/unread`, { userKey }, { headers: this.getHeaders() });
  }

  markAsRead(userKey: number, notifKeys: number[]): Observable<any> {
    return this.http.post(`${this.apiUrl}/notifications/mark-read`, { userKey, notifKeys }, { headers: this.getHeaders() });
  }

  getHistory(userKey: number, page: number = 1, pageSize: number = 10): Observable<any> {
    return this.http.post(`${this.apiUrl}/notifications/history`, { userKey, page, pageSize }, { headers: this.getHeaders() });
  }

  getUnreadCount(userKey: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/notifications/unread-count`, { userKey }, { headers: this.getHeaders() });
  }

  // ============================================
  // ADMIN ENDPOINTS (require MANAGE_NOTIFICATIONS right)
  // ============================================

  adminList(userKey: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/admin/notifications/list`, { userKey }, { headers: this.getHeaders() });
  }

  adminCreate(userKey: number, data: {
    type: NotificationType; title: string; body: string;
    priority?: number; icon?: string;
    targetType?: TargetType; targets?: NotificationTarget[];
    published: boolean; expiresAt?: string; dismissable?: boolean;
  }): Observable<any> {
    return this.http.post(`${this.apiUrl}/admin/notifications/create`, { userKey, ...data }, { headers: this.getHeaders() });
  }

  adminUpdate(userKey: number, notifKey: number, data: {
    type: NotificationType; title: string; body: string;
    priority?: number; icon?: string;
    targetType?: TargetType; targets?: NotificationTarget[];
    published: boolean; expiresAt?: string; dismissable?: boolean;
  }): Observable<any> {
    return this.http.post(`${this.apiUrl}/admin/notifications/update`, { userKey, notifKey, ...data }, { headers: this.getHeaders() });
  }

  adminDelete(userKey: number, notifKey: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/admin/notifications/delete`, { userKey, notifKey }, { headers: this.getHeaders() });
  }

  adminGetTargets(userKey: number, notifKey: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/admin/notifications/targets`, { userKey, notifKey }, { headers: this.getHeaders() });
  }

  adminGetReaders(userKey: number, notifKey: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/admin/notifications/readers`, { userKey, notifKey }, { headers: this.getHeaders() });
  }
}
